import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { ArrowLeft, Eye, EyeOff, Check, X } from 'lucide-react'
import { useUserStore } from '../../stores'
import { useI18n } from '../../i18n'
import { ResponsiveWrapper } from '../../components/layout'
import BottomActionBar from '../../components/BottomActionBar'
import orellaLogo from '../../assets/logos/orella_46A8F9_logo.png'
import { OrellaSpinner } from '../../components/OrellaLoader' // s123: the app's one spinner

export default function NewPasswordScreen() {
  const navigate = useNavigate()
  const location = useLocation()
  const { resetPassword, loading, error: storeError, clearError } = useUserStore()
  const { t } = useI18n()
  
  // Get data from navigation state
  const { userId, resetAuthToken, email } = location.state || {}
  
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  
  // Redirect if no userId
  useEffect(() => {
    if (!userId || !resetAuthToken) {
      navigate('/forgot-password', { replace: true })
    }
  }, [userId, resetAuthToken, navigate])
  
  // Clear errors on mount
  useEffect(() => {
    clearError()
    setError('')
  }, [])
  
  // Password requirements
  const requirements = [
    { key: 'length', test: (p) => p.length >= 8, label: t('auth.passwordMinLength') },
    { key: 'match', test: (p) => p && confirmPassword && p === confirmPassword, label: t('auth.passwordsMatch') },
  ]
  
  const allRequirementsMet = requirements.every(req => req.test(password))
  
  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    clearError()
    
    if (!password) {
      setError(t('auth.passwordRequired'))
      return
    }
    
    if (password.length < 8) {
      setError(t('auth.passwordTooShort'))
      return
    }
    
    if (password !== confirmPassword) {
      setError(t('auth.passwordsMismatch'))
      return
    }
    
    const result = await resetPassword(userId, password, resetAuthToken)
    
    if (result.success) {
      setSuccess(true)
      // Redirect to sign in after a short delay
      setTimeout(() => {
        navigate('/signin', { replace: true })
      }, 2000)
    } else {
      setError(result.error || t('auth.resetFailed'))
    }
  }
  
  if (!userId || !resetAuthToken) {
    return null
  }
  
  // Success state
  if (success) {
    return (
      <ResponsiveWrapper className="bg-white">
        <div className="screen-min-h bg-white px-6 md:px-10 lg:px-12 py-8 flex flex-col items-center justify-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6">
            <Check className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2 text-center">
            {t('auth.passwordResetSuccess')}
          </h1>
          <p className="text-gray-500 text-center mb-2">
            {t('auth.redirectingToSignIn')}
          </p>
          {/* PROD-1345: a reset signs the account out everywhere; say so. */}
          <p className="text-gray-500 text-center mb-8" role="status">
            {t('auth.signedOutAllDevices')}
          </p>
          <OrellaSpinner size={24} />
        </div>
      </ResponsiveWrapper>
    )
  }
  
  return (
    <ResponsiveWrapper className="bg-white" bottomInset={false}>
      <div className="screen-min-h bg-white px-6 md:px-10 lg:px-12 pt-8 flex flex-col">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button 
            onClick={() => navigate('/forgot-password')}
            className="p-2 -ml-2 text-gray-600 hover:text-gray-800"
          >
            <ArrowLeft size={24} />
          </button>
          <img 
            src={orellaLogo} 
            alt="Orella" 
            className="h-8 md:h-10 object-contain" 
          />
        </div>
        
        {/* Title */}
        <h1 className="text-2xl md:text-3xl font-bold text-orella-500 mb-2">
          {t('auth.newPasswordTitle')}
        </h1>
        <p className="text-gray-500 mb-8 md:text-lg">
          {t('auth.newPasswordDesc')}
        </p>
        
        {/* Error Message */}
        {(error || storeError) && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
            {error || storeError}
          </div>
        )}
        
        {/* Form */}
        {/* PROD-1194: the form fills the column; Save (and Cancel) are pinned at the bottom. */}
        <form onSubmit={handleSubmit} className="flex-1 flex flex-col">
          <div className="space-y-5">
          {/* New Password */}
          <div>
            <label className="block text-sm text-gray-500 mb-1">
              {t('auth.newPassword')}
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 pr-12 border border-gray-200 rounded-xl focus:outline-none focus:border-orella-500"
                placeholder={t('auth.passwordPlaceholder')}
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>
          
          {/* Confirm Password */}
          <div>
            <label className="block text-sm text-gray-500 mb-1">
              {t('auth.confirmPassword')}
            </label>
            <div className="relative">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-4 py-3 pr-12 border border-gray-200 rounded-xl focus:outline-none focus:border-orella-500"
                placeholder={t('auth.passwordPlaceholder')}
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400"
              >
                {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>
          
          {/* Password Requirements */}
          <div className="space-y-2 pt-2">
            {requirements.map(req => (
              <div 
                key={req.key}
                className={`flex items-center gap-2 text-sm ${
                  req.test(password) ? 'text-green-600' : 'text-gray-400'
                }`}
              >
                {req.test(password) ? (
                  <Check size={16} className="flex-shrink-0" />
                ) : (
                  <X size={16} className="flex-shrink-0" />
                )}
                <span>{req.label}</span>
              </div>
            ))}
          </div>
          
          </div>
          
          <BottomActionBar className="-mx-6 md:-mx-10 lg:-mx-12 [--bar-px:24px]">
            {/* Submit Button */}
            <button 
              type="submit"
              disabled={loading || !allRequirementsMet}
              className="w-full py-4 bg-orella-500 text-white font-semibold rounded-full disabled:opacity-50 transition-opacity breathe-blue"
            >
              {loading ? t('common.saving') : t('auth.savePassword')}
            </button>
            
            {/* Cancel Button */}
            <button 
              type="button"
              onClick={() => navigate('/signin')}
              className="w-full py-4 mt-3 bg-gray-200 text-gray-600 font-semibold rounded-full"
            >
              {t('common.cancel')}
            </button>
          </BottomActionBar>
        </form>
      </div>
    </ResponsiveWrapper>
  )
}
